import FormControlLabel from '@material-ui/core/FormControlLabel';
import List from '@material-ui/core/List';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import Box from '@mui/material/Box';
import { getData, isValid } from 'clients/Clients';
import { createReply, getCommentsBySkuCode, getReply } from 'clients/WebServiceClient';
import ActionButton from 'components-v2/atoms/ActionButton';
import LoadMoreWrapper from 'components-v2/atoms/LoadMoreWrapper';
import ReadMoreWrapper from 'components-v2/atoms/ReadMoreWrapper';
import CommentItem from 'components-v2/mocules/CommentList/CommentItem';
import { QA_BTN_ICON, QA_TITLE_ICON } from 'constants/Images';
import { useIsInViewportOnce } from 'hooks/useIsInViewportOnce';
import useModalV2 from 'hooks/useModalV2';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import RegisteredQuestionForm from './RegisteredQuestionForm';
import UnregisteredQuestionForm from './UnregisteredQuestionForm';
import { LIMIT_HEIGHT_COMMENTS, LIMIT_ITEMS, defaultData } from './constants';
import styles from './styles.module.css';

const defaultActiveComment = {
  id: null,
  type: 'idle',
};

export const QuestionAndAnswer = ({ user, targetCode, priceFeedback }) => {
  const commentRef = useRef();
  const router = useRouter();
  const [sort, setSort] = useState(router.query.sort || 'NEWEST');
  const [offset, setOffset] = useState(0);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [activeComment, setActiveComment] = useState(defaultActiveComment);
  const [data, setData] = useState(defaultData);

  const hasComments = data.totalCommentVisible > 0;
  const hasMoreCommentsToLoad = data.totalCommentVisible < data.totalComment;
  const isCommentListBelowHeightLimit = (commentRef.current?.clientHeight || 0) < LIMIT_HEIGHT_COMMENTS;

  const { elementRef } = useIsInViewportOnce({
    callback: async function initComments() {
      fetchComments({ sort, offset, commentID: +String(router.query.commentID) });
    },
  });

  useEffect(() => {
    if (router.query.commentID) {
      // CHEAT: use setTimeout to delay after the DOM is rendered, and scroll.
      // Without it, it will scroll to the comment section and jump back to top.
      setTimeout(() => {
        elementRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, []);

  async function fetchComments({ limit = LIMIT_ITEMS, offset = 0, sort = 'NEWEST', commentID = 0 }) {
    let mergedComments = [];

    const rs = await getCommentsBySkuCode({
      isAuth: false,
      skuCode: targetCode,
      sort,
      offset,
      limit,
      getTotal: true,
      ...(commentID && { excludeID: String(commentID) }),
    });

    if (commentID) {
      const getComment = await getCommentsBySkuCode({
        isAuth: false,
        skuCode: targetCode,
        sort,
        offset,
        limit,
        q: {
          commentID: +commentID,
        },
      });

      if (isValid(getComment)) {
        const [commentData] = getData(getComment);
        setData({
          ...data,
          totalComment: 1,
        });
        mergedComments.push(commentData);
      }
    }

    if (!isValid(rs) && !commentID) {
      setData(defaultData);
      setIsMoreLoading(false);
      return;
    }

    const comments = getData(rs);

    setData((prevData) => {
      mergedComments = offset === 0 && !commentID ? comments : [...mergedComments, ...prevData.comments, ...comments];

      return {
        comments: mergedComments,
        totalComment: rs.total || mergedComments.length,
        totalCommentVisible: mergedComments.length,
      };
    });
    setIsMoreLoading(false);
  }

  async function fetchReply({ offset = 0, limit = 5, parentCommentID = null }) {
    const rs = await getReply({
      isAuth: true,
      parentCommentID,
      offset,
      limit,
    });

    if (!isValid(rs)) {
      return;
    }

    const result = getData(rs);
    const cloneComments = data.comments;

    cloneComments.forEach((comment) => {
      if (comment.commentID === parentCommentID) {
        comment.replies.push(...result);
      }
    });

    setData({ ...data, comments: cloneComments });
  }

  function handleSort(event) {
    const queryParams = { ...router.query };
    if (router.query.commentID) delete queryParams.commentID;

    const { value } = event.target;
    setSort(value);
    setOffset(0);

    router.push(
      {
        pathname: router.pathname,
        query: { ...queryParams, sort: value },
      },
      undefined,
      { shallow: true, scroll: false },
    );

    fetchComments({ sort: value, offset: 0, commentID: 0 });
  }

  function handleLoadMoreComment(num) {
    setIsMoreLoading(true);

    const newOffset = offset + LIMIT_ITEMS;
    setOffset(newOffset);
    fetchComments({ sort, offset: newOffset, commentID: +String(router.query.commentID) });
  }

  function handleLoadMoreReply(data) {
    const { restNumber, parentCommentID, totalReply } = data;
    const offsetCalc = totalReply - restNumber;

    fetchReply({ offset: offsetCalc, parentCommentID });
  }

  async function handleReply(data, parentCommentID) {
    const rs = await createReply({
      content: data.reply,
      parentCommentID,
      images: data.images,
    });

    if (!isValid(rs)) {
      toast.error(rs.message || 'Có lỗi xảy ra, vui lòng thử lại');
      return;
    }

    toast.success('Cảm ơn bạn đã gửi ý kiến cho thuocsi.vn. Ý kiến của bạn đang chờ duyệt.', { autoClose: 3000 });
    setActiveComment(defaultActiveComment);
  }

  return (
    <section ref={elementRef} className={styles.comment}>
      <Box className={styles.actionComment}>
        <Box className={styles.titleComment}>
          <Box className={styles.logo}>
            <Image src={QA_TITLE_ICON} width={32} height={32} loading="lazy" />
          </Box>
          <Typography className={styles.headingComment}>
            Ý kiến về sản phẩm {hasComments && <span>({data.totalComment || data.totalCommentVisible} ý kiến)</span>}
          </Typography>
        </Box>
        {hasComments && <SendCommentBtn user={user} targetCode={targetCode} />}
      </Box>
      {priceFeedback && <Box className={styles.priceFeedback}>{priceFeedback}</Box>}
      {(hasComments || sort === 'IS_HAVE_IMAGE') && (
        <Box className={styles.filterComment}>
          <Typography className={styles.filterHeading}>Lọc theo</Typography>
          <RadioGroup className={styles.btnGroup} row value={sort} onChange={handleSort}>
            <FormControlLabel value="NEWEST" control={<Radio />} label="Mới nhất" />
            <FormControlLabel value="OLDEST" control={<Radio />} label="Cũ nhất" />
            <FormControlLabel value="IS_HAVE_IMAGE" control={<Radio />} label="Có ảnh" />
          </RadioGroup>
        </Box>
      )}
      <Box className={styles.mainComment}>
        {hasComments ? (
          <ReadMoreWrapper
            disabled={hasMoreCommentsToLoad || isCommentListBelowHeightLimit}
            isOpen={hasMoreCommentsToLoad}
            maxHeight={LIMIT_HEIGHT_COMMENTS}
          >
            <LoadMoreWrapper
              isLoading={isMoreLoading}
              total={data.totalComment}
              totalVisible={data.totalCommentVisible}
              onLoadMoreComment={handleLoadMoreComment}
            >
              <List innerRef={commentRef} className={styles.commentList}>
                {data.comments.map((comment) => {
                  return (
                    <CommentItem
                      key={comment.commentID}
                      id={comment.commentID}
                      parentCommentID={comment.commentID}
                      activeComment={activeComment}
                      setActiveComment={setActiveComment}
                      commenter={comment.commenter}
                      content={comment.content}
                      images={comment.images}
                      createdTime={comment.createdTime}
                      replies={comment.replies}
                      totalReply={comment.totalReplies || 0}
                      onReply={handleReply}
                      onLoadMoreReply={handleLoadMoreReply}
                    />
                  );
                })}
              </List>
            </LoadMoreWrapper>
          </ReadMoreWrapper>
        ) : sort === 'IS_HAVE_IMAGE' ? (
          <Box className={styles.emptyComment}>
            <Typography className={styles.textEmptyComment}>Chưa có ý kiến nào có hình ảnh.</Typography>
          </Box>
        ) : (
          <Box className={styles.emptyComment}>
            <SendCommentBtn user={user} targetCode={targetCode} />
            <Typography className={styles.textEmptyComment}>Bạn có thắc mắc về sản phẩm? Gửi câu hỏi để được giải đáp ngay!</Typography>
          </Box>
        )}
      </Box>
    </section>
  );
};

function SendCommentBtn({ user, targetCode }) {
  const [isOpenQuestionModal, toggleQuestionModal] = useModalV2(false);
  const [isNotLoginModal, toggleNotLoginModal] = useModalV2(false);

  function handleToggleModal() {
    if (user) {
      toggleQuestionModal();
      return;
    }

    toggleNotLoginModal();
  }

  return (
    <>
      <ActionButton
        iconLeft={<Image src={QA_BTN_ICON} width={24} height={24} loading="lazy" />}
        size="small"
        variant="contained"
        color="greenish"
        onClick={handleToggleModal}
      >
        Gửi ý kiến
      </ActionButton>
      <RegisteredQuestionForm targetCode={targetCode} isOpen={isOpenQuestionModal} onClose={toggleQuestionModal} />
      <UnregisteredQuestionForm targetCode={targetCode} isOpen={isNotLoginModal} onClose={toggleNotLoginModal} />
    </>
  );
}
